import BookingDetails from '@components/BookingDetails/BookingDetails';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import {
  ButtonsWrapper,
  ExternalWrapper,
  InnerContainer,
  LeftButtonsWrapper,
  RightContainer,
  StepContainer,
} from '@components/Steps/Steps.styles';
import {
  CheckIcon,
  CloseIcon,
  InformationIcon,
  LuxButton,
  LuxLoader,
  LuxModal,
  LuxModalFooter,
  LuxModalHeader,
  LuxTypography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SubtitleWrapper,
  ThankYouMessageWrapper,
  TypWrapper,
} from '@components/ThankYouPage/ThankYouPage.styles';
import { CancelConfirmCard } from './CancelConfirmPage.styles';
import {
  CancelConfirmPageMode,
  ConfirmCancelErrorProps,
} from './CancelConfirmPage.interface';
import {
  getCancelConfirmErrorParam,
  getCancelConfirmRouterParams,
  getCancelConfirmStatusCode,
  getHasCancelConfirmSecondaryButtonConfig,
  getIsCancelConfirmConfirmDisabled,
  getShouldRenderCancelConfirmButtonsWrapper,
  isCancelConfirmAppointmentConfirmed,
  toCancelConfirmPatientAppointmentData,
} from './CancelConfirmPage.utils';
import useGetAppointmentData from '@hooks/useGetAppointmentData';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { useHistory } from 'react-router-dom';
import useConfirmAppointment from '@hooks/useConfirmAppointment';
import { conditionallyApplyUpperCase, resetApp } from '@utils/functions';
import useDeleteAppointment from '@hooks/useDeleteAppointment';
import { Helmet } from 'react-helmet';
import AppointmentErrorModal from '@components/AppointmentErrorModal/AppointmentErrorModal';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import useAnalytics, { AnalyticsActionType } from '@hooks/useAnalytics';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';
import { BrandNameLabelList } from '@utils/consts/brandNameList';
import { PostSubmissionError } from '@utils/enums/PostSubmissionError.enum';

const CancelConfirmPage = () => {
  // #region Base hooks
  const { t } = useTranslation();
  const { Urls } = useUrlsContext();
  const theme = useTheme();
  const history = useHistory();
  const { trackPageAnalytics, trackEventAnalytics } = useAnalytics();
  const btwXsAndSm = useMediaQuery(theme.breakpoints.between('xs', 'sm'));
  // #endregion

  // #region Store selectors
  const appointmentIdFromStore = useOnlineBookingStore(
    state => state.appointmentId,
  );
  const brandName = useOnlineBookingStore(state => state.brandName);
  const generalData = useOnlineBookingStore(state => state.generalConfig);
  const hideBackToBrandHomePage = useOnlineBookingStore(
    state => state.hideBackToBrandHomePage,
  );
  const integrationMode = useOnlineBookingStore(state => state.integrationMode);
  const links = useOnlineBookingStore(state => state.links);
  const marketingAndConsent = useOnlineBookingStore(
    state => state.formConfigs?.customFieldsMarketing,
  );
  const practiceId = useOnlineBookingStore(state => state.practiceId);
  const setCurrentPatient = useOnlineBookingStore(
    state => state.setCurrentPatient,
  );
  const getPatients = usePatientStore(state => state.getPatients);
  const patients = getPatients();
  const updatePatient = usePatientStore(state => state.updatePatient);
  // #endregion

  // #region URL params
  const routerParams = useMemo(() => {
    return getCancelConfirmRouterParams(integrationMode);
  }, [integrationMode]);

  const appointmentIdFromUrl = useMemo(
    () => routerParams.get('appointmentId'),
    [routerParams],
  );

  const modeParamFromUrl = useMemo(
    () => routerParams.get('mode'),
    [routerParams],
  );

  const errorParamFromUrl = useMemo(() => {
    return getCancelConfirmErrorParam();
  }, []);
  // #endregion

  // #region Derived state
  const appointmentId = appointmentIdFromStore || appointmentIdFromUrl;
  const [mode, setMode] = useState<CancelConfirmPageMode | string | null>(
    () => modeParamFromUrl,
  );
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [modalSize, setModalSize] = useState<'small' | 'medium'>('medium');

  const isCancelMode = modeParamFromUrl === CancelConfirmPageMode.CANCEL;
  const isConfirmMode = modeParamFromUrl === CancelConfirmPageMode.CONFIRM;
  const isCancelledError = errorParamFromUrl === PostSubmissionError.CANCELLED;
  const isCompleteError = errorParamFromUrl === PostSubmissionError.COMPLETE;
  const isPastCancelError =
    isCancelMode && errorParamFromUrl === PostSubmissionError.PAST;
  // #endregion

  // #region Queries and mutations
  const { data, isLoading: isLoadingData } = useGetAppointmentData({
    appointmentId,
  });

  const isConfirmedAppointment = isCancelConfirmAppointmentConfirmed(
    data?.appointmentStatus,
  );

  const patientId = useMemo(() => {
    return data?.AppointmentExam?.patientId || undefined;
  }, [data]);

  const {
    mutate: confirmAppointment,
    isSuccess: isSuccessConf,
    isLoading: isLoadingConf,
    isError: isErrorConf,
    error: errorConfRaw,
  } = useConfirmAppointment({
    appointmentId,
    patientId,
  });
  const errorConf = errorConfRaw as ConfirmCancelErrorProps | undefined;

  const {
    mutate: deleteAppointment,
    isSuccess: isSuccessDel,
    isLoading: isLoadingDel,
    isError: isErrorDel,
    error: errorDelRaw,
  } = useDeleteAppointment({
    appointmentId,
    practiceId,
  });
  const errorDel = errorDelRaw as ConfirmCancelErrorProps | undefined;
  // #endregion

  // #region Mode transitions
  useEffect(() => {
    if (!isLoadingConf && isSuccessConf) {
      setMode(CancelConfirmPageMode.CONFIRMED);
    }
  }, [isLoadingConf, isSuccessConf]);

  useEffect(() => {
    if (!isLoadingDel && isSuccessDel) {
      setMode(CancelConfirmPageMode.CANCELED);
    }
  }, [isLoadingDel, isSuccessDel]);

  useEffect(() => {
    if (isErrorDel || isErrorConf) {
      setMode(CancelConfirmPageMode.ERROR);
    }
  }, [isErrorDel, isErrorConf]);

  useEffect(() => {
    // Explicit OPUI cancellation must win over appointment status from GET.
    if (isCancelledError || isPastCancelError) {
      setMode(CancelConfirmPageMode.ERROR);
      return;
    }

    // Re-opening an already confirmed appointment from a confirm email is success.
    if (isConfirmMode && (isConfirmedAppointment || isCompleteError)) {
      setMode(CancelConfirmPageMode.CONFIRMED);
      return;
    }

    // The same finalized states block cancel actions and other manage attempts.
    if (isConfirmedAppointment || isCompleteError) {
      setMode(CancelConfirmPageMode.ERROR);
    }
  }, [
    isCancelledError,
    isCompleteError,
    isConfirmMode,
    isConfirmedAppointment,
    isPastCancelError,
  ]);
  // #endregion

  // #region Side effects
  useEffect(() => {
    if (mode === CancelConfirmPageMode.CANCELED) {
      trackPageAnalytics({
        page: FlowsPages.CANCELED,
        patients,
      });
    }
  }, [mode]);

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia('(min-width: 576px)');
    const handleSizeChange = (e: MediaQueryListEvent) => {
      setModalSize(e.matches ? 'medium' : 'small');
    };

    mediaQuery.addEventListener('change', handleSizeChange);
    return () => mediaQuery.removeEventListener('change', handleSizeChange);
  }, []);

  useEffect(() => {
    setCurrentPatient(0);
  }, [setCurrentPatient]);

  useEffect(() => {
    if (data) {
      console.log('##data', data);

      updatePatient(
        0,
        FormIds.AppointmentType,
        toCancelConfirmPatientAppointmentData(data, brandName, t),
      );
    }
  }, [data, updatePatient]);
  // #endregion

  // #region Render helpers
  const actionCode = useMemo(() => {
    return modeParamFromUrl || undefined;
  }, [modeParamFromUrl]);

  const statusCode = useMemo(() => {
    return getCancelConfirmStatusCode({
      errorConf,
      errorDel,
      isCancelledError,
      isCompleteError,
      isConfirmedAppointment,
      isPastCancelError,
    });
  }, [
    errorDel,
    errorConf,
    isCancelledError,
    isCompleteError,
    isConfirmedAppointment,
    isPastCancelError,
  ]);

  const isConfirmDisabled = useMemo(() => {
    return getIsCancelConfirmConfirmDisabled(marketingAndConsent);
  }, [marketingAndConsent]);

  const hasSecondaryButtonConfig = useMemo(() => {
    return getHasCancelConfirmSecondaryButtonConfig(generalData);
  }, [generalData]);

  const shouldRenderButtonsWrapper = useMemo(() => {
    return getShouldRenderCancelConfirmButtonsWrapper(
      mode,
      hasSecondaryButtonConfig,
    );
  }, [mode, hasSecondaryButtonConfig]);
  // #endregion

  return (
    <>
      {isLoadingData ? (
        <ExternalWrapper
          backgroundColor={
            brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
          }
        >
          <LuxLoader height="4rem" />
        </ExternalWrapper>
      ) : (
        <>
          {mode && (
            <Helmet>
              <title>
                {mode === CancelConfirmPageMode.CONFIRM
                  ? t('wcag.ob_manage_confirm_title')
                  : mode === CancelConfirmPageMode.CANCEL
                    ? t('wcag.ob_manage_cancel_title')
                    : null}
              </title>
            </Helmet>
          )}
          <ExternalWrapper
            backgroundColor={
              brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
            }
          >
            {mode === CancelConfirmPageMode.ERROR && (
              <AppointmentErrorModal
                statusCode={statusCode}
                actionCode={actionCode}
              />
            )}
            {mode !== CancelConfirmPageMode.ERROR && (
              <TypWrapper>
                {mode !== CancelConfirmPageMode.CANCELED && (
                  <ThankYouMessageWrapper>
                    <Stack
                      style={{
                        width: '2.75rem',
                        height: '2.75rem',
                        borderRadius: '100%',
                        border: `1px solid ${mode === CancelConfirmPageMode.CONFIRMED ? '#23AE3133' : theme.palette.LUX.obTokens.bordersColorFunctionalInfo}`,
                        backgroundColor: `${mode === CancelConfirmPageMode.CONFIRMED ? '#E2FCE5' : theme.palette.LUX.obTokens.bgColorFunctionalInfo}`,
                      }}
                      justifyContent="center"
                      alignItems="center"
                    >
                      {mode === CancelConfirmPageMode.CONFIRMED ? (
                        <CheckIcon color={theme.palette.LUX.content.primary} />
                      ) : (
                        <InformationIcon
                          color={theme.palette.LUX.content.primary}
                        />
                      )}
                    </Stack>
                    {mode === CancelConfirmPageMode.CONFIRMED ? (
                      <SubtitleWrapper>
                        <LuxTypography variant="heading3">
                          {t('thankYouMessages.appointmentBooked')}
                        </LuxTypography>
                        <LuxTypography variant="textMdRegular">
                          {t('thankYouMessages.cantWaitToSeeYou')}
                        </LuxTypography>
                      </SubtitleWrapper>
                    ) : (
                      <LuxTypography variant="heading3">
                        {t('manageAppointment.manage')}
                      </LuxTypography>
                    )}
                  </ThankYouMessageWrapper>
                )}
                <InnerContainer
                  canceledPage={
                    mode === CancelConfirmPageMode.CANCELED ||
                    mode === CancelConfirmPageMode.CONFIRMED
                  }
                >
                  {mode !== CancelConfirmPageMode.CONFIRMED && (
                    <StepContainer
                      borderColor={
                        theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
                      }
                    >
                      {mode !== CancelConfirmPageMode.CANCELED &&
                        mode !== CancelConfirmPageMode.CONFIRMED && (
                          <CardTitleWrapper
                            borderColor={
                              theme.palette.LUX.obTokens
                                ?.bordersColorNeutralSecondary
                            }
                          >
                            <LuxTypography variant="heading4">
                              {mode === CancelConfirmPageMode.CONFIRM
                                ? t('manageAppointment.confirmTitle')
                                : t('manageAppointment.cancelTitle')}
                            </LuxTypography>
                          </CardTitleWrapper>
                        )}
                      <CancelConfirmCard
                        canceled={mode === CancelConfirmPageMode.CANCELED}
                      >
                        {mode === CancelConfirmPageMode.CANCELED && (
                          <Stack
                            style={{
                              width: '2.75rem',
                              height: '2.75rem',
                              borderRadius: '100%',
                              backgroundColor:
                                theme.palette.LUX.obTokens
                                  .bgColorFunctionalError,
                              border: `1px solid ${theme.palette.LUX.obTokens.bordersColorFunctionalError}`,
                            }}
                            justifyContent="center"
                            alignItems="center"
                          >
                            <CloseIcon
                              color={theme.palette.LUX.content.primary}
                            />
                          </Stack>
                        )}
                        <LuxTypography
                          variant={
                            mode === CancelConfirmPageMode.CANCEL
                              ? 'heading4'
                              : 'heading3'
                          }
                          fontSize={
                            mode === CancelConfirmPageMode.CANCEL
                              ? '1.125rem'
                              : undefined
                          }
                        >
                          {mode === CancelConfirmPageMode.CANCELED
                            ? t('manageAppointment.canceledTitle')
                            : mode === CancelConfirmPageMode.CANCEL
                              ? t('manageAppointment.cancel')
                              : t('manageAppointment.confirm')}
                        </LuxTypography>
                        <LuxTypography variant={'textMdRegular'}>
                          {mode === CancelConfirmPageMode.CANCELED
                            ? t('manageAppointment.canceledSubtitle')
                            : mode === CancelConfirmPageMode.CANCEL
                              ? t('manageAppointment.pleaseCancel')
                              : t('manageAppointment.pleaseConfirm')}
                        </LuxTypography>
                      </CancelConfirmCard>
                    </StepContainer>
                  )}
                  {mode !== CancelConfirmPageMode.CANCELED && (
                    <RightContainer>
                      <BookingDetails
                        hideDownloadButton
                        hideEditButton
                        isFullRecap={mode === CancelConfirmPageMode.CONFIRMED}
                        manageOrRescheduleMode
                      />
                    </RightContainer>
                  )}
                </InnerContainer>
              </TypWrapper>
            )}
          </ExternalWrapper>
          {shouldRenderButtonsWrapper && (
            <ButtonsWrapper
              step={mode === CancelConfirmPageMode.CANCELED ? 1 : 0}
              fromTyp={mode === CancelConfirmPageMode.CANCELED}
              onlySecondary={!brandName || !links?.homePage}
            >
              {
                /*first buttons condition case */
                ((hasSecondaryButtonConfig &&
                  (mode === CancelConfirmPageMode.CANCELED ||
                    mode === CancelConfirmPageMode.CONFIRMED)) ||
                  /* second buttons condition case */
                  (brandName &&
                    links?.homePage &&
                    mode === CancelConfirmPageMode.CANCELED)) && (
                  <LeftButtonsWrapper>
                    {hasSecondaryButtonConfig &&
                      (mode === CancelConfirmPageMode.CANCELED ||
                        mode === CancelConfirmPageMode.CONFIRMED) && (
                        <LuxButton
                          variant="secondary"
                          iconStart="HomeIcon"
                          onClick={() =>
                            window.open(
                              generalData?.customization?.secondaryButtonLink,
                              '_self',
                            )
                          }
                        >
                          <LuxTypography
                            variant={'textMdRegular'}
                            fontWeight={brandName && '700!important'}
                          >
                            {generalData?.customization?.secondaryButtonLabel ??
                              t('generalCta.toEcp')}
                          </LuxTypography>
                        </LuxButton>
                      )}
                    {brandName &&
                      links?.homePage &&
                      !hideBackToBrandHomePage &&
                      mode === CancelConfirmPageMode.CANCELED && (
                        <LuxButton
                          variant="secondary"
                          iconStart="LogoutIcon"
                          onClick={() =>
                            links.homePage &&
                            (window.location.href = links?.homePage)
                          }
                        >
                          <LuxTypography
                            variant={'textMdRegular'}
                            fontWeight={brandName && '700!important'}
                          >
                            {t('generalCta.backToBrandHomePage', {
                              brandName:
                                BrandNameLabelList[
                                  brandName as keyof typeof BrandNameLabelList
                                ],
                            }).toUpperCase()}
                          </LuxTypography>
                        </LuxButton>
                      )}
                  </LeftButtonsWrapper>
                )
              }
              {mode !== CancelConfirmPageMode.CONFIRMED && (
                <LuxButton
                  disabled={
                    mode === CancelConfirmPageMode.CONFIRM && isConfirmDisabled
                  }
                  variant="primary"
                  onClick={() => {
                    switch (mode) {
                      case CancelConfirmPageMode.CANCELED:
                        resetApp(true);
                        history.push(Urls.APPOINTMENT_TYPE);
                        break;
                      case CancelConfirmPageMode.CONFIRM:
                        confirmAppointment();
                        break;
                      case CancelConfirmPageMode.CANCEL:
                      default:
                        setOpenCancelModal(true);
                        break;
                    }
                  }}
                  style={{
                    ...(!brandName && mode === CancelConfirmPageMode.CANCEL
                      ? {
                          backgroundColor: `${theme.palette.LUX.obTokens?.bgColorFunctionalError}`,
                          color: '#001625',
                          border: `1px solid ${theme.palette.LUX.obTokens?.bordersColorFunctionalError}`,
                        }
                      : {}),
                  }}
                  iconStart={
                    !brandName && mode === CancelConfirmPageMode.CANCEL
                      ? 'CloseIcon'
                      : mode === CancelConfirmPageMode.CANCELED
                        ? 'CalendarIcon'
                        : undefined
                  }
                >
                  <LuxTypography
                    variant={'textMdRegular'}
                    fontWeight={brandName && '700!important'}
                  >
                    {mode === CancelConfirmPageMode.CANCEL
                      ? conditionallyApplyUpperCase(
                          t('manageAppointment.cancel'),
                          brandName,
                        )
                      : mode === CancelConfirmPageMode.CANCELED
                        ? conditionallyApplyUpperCase(
                            t('manageAppointment.bookNew'),
                            brandName,
                          )
                        : conditionallyApplyUpperCase(
                            t('manageAppointment.confirm'),
                            brandName,
                          )}
                  </LuxTypography>
                </LuxButton>
              )}
            </ButtonsWrapper>
          )}
          <LuxModal
            open={openCancelModal}
            size={modalSize}
            onClose={() => setOpenCancelModal(false)}
            hideFooterShadow
            dividerFooter
            sx={{
              '& .lux-modal-header': {
                padding: '2.5rem 1.75rem 0',
              },
              '& .lux-modal-content-wrapper': {
                padding: '0 1.5rem',
              },
            }}
            header={
              <LuxModalHeader
                darkMode={false}
                enableCloseIcon
                onClose={() => setOpenCancelModal(false)}
                title={
                  <LuxTypography variant="heading3">
                    {t('manageAppointment.cancel')}
                  </LuxTypography>
                }
              />
            }
            footer={
              <LuxModalFooter
                buttonsFlexDirection={btwXsAndSm ? 'column' : 'row'}
                justifyContent="space-between"
                onSecondaryButtonClick={() => {
                  setOpenCancelModal(false);
                }}
                onPrimaryButtonClick={() => {
                  deleteAppointment();
                  setOpenCancelModal(false);
                  //analytics event track
                  trackEventAnalytics({
                    action: AnalyticsActionType.CANCEL_APPOINTMENT,
                  });
                }}
                primaryButtonText={
                  brandName
                    ? `${t('manageAppointment.yes').toUpperCase()}`
                    : `${t('manageAppointment.yes')}`
                }
                secondaryButtonText={
                  brandName
                    ? `${t('manageAppointment.no').toUpperCase()}`
                    : `${t('manageAppointment.no')}`
                }
                primaryButtonSx={{
                  width: {
                    xs: '100%',
                    sm: 'auto',
                  },
                }}
                secondaryButtonSx={{
                  width: {
                    xs: '100%',
                    sm: 'auto',
                  },
                }}
              />
            }
          >
            <LuxTypography variant={'textXs'} fontSize={'0.875rem'}>
              {t('manageAppointment.sureCancel')}
            </LuxTypography>
          </LuxModal>
        </>
      )}
    </>
  );
};

export default CancelConfirmPage;
